import {
  User,
  TeamMember,
  Client,
  BillingInfo,
  NotificationPreferences,
  SecuritySettings,
  ProfileStats,
  ActivityLog,
} from './types';

// Current user data (Agency account type for comprehensive features)
export const mockUser: User = {
  id: 'user-001',
  email: 'sarah.johnson@digitalboost.agency',
  firstName: 'Sarah',
  lastName: 'Johnson',
  phone: '+1-555-123-4567',
  profilePhoto:
    'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
  jobTitle: 'Digital Marketing Director',
  companyName: 'DigitalBoost Agency',
  industry: 'Digital Marketing',
  website: 'https://digitalboost.agency',
  bio: 'Experienced digital marketing professional specializing in SEO strategy, link building campaigns, and client growth optimization. Leading a team of 12 marketing specialists serving 50+ clients.',
  location: {
    country: 'United States',
    state: 'New York',
    city: 'New York',
    timezone: 'America/New_York',
    postalCode: '10001',
  },
  languagePreference: 'English',
  accountType: 'agency', // Switch between 'individual', 'team', 'agency' for testing
  accountStatus: 'active',
  createdAt: '2023-03-15T10:30:00Z',
  lastLoginAt: '2024-08-20T14:22:00Z',
  isEmailVerified: true,
  isPhoneVerified: true,
  isBusinessVerified: true,
  twoFactorEnabled: true,
  profileCompletionPercentage: 95,
  securityScore: 87,
  preferences: {
    theme: 'light',
    language: 'English',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    dashboardLayout: 'comfortable',
    autoSave: true,
    showTutorials: false,
    enableAnimations: true,
    defaultView: 'grid',
  },
};

// Team members (for team/agency accounts)
export const mockTeamMembers: TeamMember[] = [
  {
    id: 'member-001',
    email: 'michael.chen@digitalboost.agency',
    firstName: 'Michael',
    lastName: 'Chen',
    role: {
      id: 'role-admin',
      name: 'admin',
      displayName: 'Administrator',
      description: 'Full access to all features and settings',
      permissions: [
        'manage_orders',
        'manage_billing',
        'manage_team',
        'view_analytics',
        'manage_clients',
      ],
      accessLevel: 90,
      canManageTeam: true,
      canManageBilling: true,
      canManageClients: true,
      canViewAnalytics: true,
      canCreateOrders: true,
      canApproveOrders: true,
    },
    status: 'active',
    invitedAt: '2023-04-01T09:00:00Z',
    invitedBy: 'user-001',
    lastActiveAt: '2024-08-20T13:45:00Z',
    profilePhoto:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    department: 'Operations',
    jobTitle: 'Operations Manager',
    accessLevel: 90,
    loginCount: 245,
    lastLoginLocation: 'New York, NY',
  },
  {
    id: 'member-002',
    email: 'jessica.rodriguez@digitalboost.agency',
    firstName: 'Jessica',
    lastName: 'Rodriguez',
    role: {
      id: 'role-editor',
      name: 'editor',
      displayName: 'Senior Editor',
      description: 'Can manage orders and communicate with clients',
      permissions: ['manage_orders', 'view_analytics', 'communicate_clients'],
      accessLevel: 70,
      canManageTeam: false,
      canManageBilling: false,
      canManageClients: true,
      canViewAnalytics: true,
      canCreateOrders: true,
      canApproveOrders: false,
    },
    status: 'active',
    invitedAt: '2023-05-15T10:30:00Z',
    invitedBy: 'user-001',
    lastActiveAt: '2024-08-20T11:30:00Z',
    profilePhoto:
      'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face',
    department: 'Client Success',
    jobTitle: 'Senior Account Manager',
    accessLevel: 70,
    loginCount: 189,
    lastLoginLocation: 'Brooklyn, NY',
  },
  {
    id: 'member-003',
    email: 'david.wilson@digitalboost.agency',
    firstName: 'David',
    lastName: 'Wilson',
    role: {
      id: 'role-viewer',
      name: 'viewer',
      displayName: 'Analyst',
      description: 'Read-only access to analytics and reports',
      permissions: ['view_analytics', 'view_orders'],
      accessLevel: 30,
      canManageTeam: false,
      canManageBilling: false,
      canManageClients: false,
      canViewAnalytics: true,
      canCreateOrders: false,
      canApproveOrders: false,
    },
    status: 'active',
    invitedAt: '2023-06-10T14:15:00Z',
    invitedBy: 'user-001',
    lastActiveAt: '2024-08-19T16:20:00Z',
    department: 'Analytics',
    jobTitle: 'Data Analyst',
    accessLevel: 30,
    loginCount: 156,
    lastLoginLocation: 'Queens, NY',
  },
  {
    id: 'member-004',
    email: 'emma.garcia@gmail.com',
    firstName: 'Emma',
    lastName: 'Garcia',
    role: {
      id: 'role-editor',
      name: 'editor',
      displayName: 'Content Editor',
      description: 'Can manage content orders and client communication',
      permissions: ['manage_orders', 'communicate_clients'],
      accessLevel: 60,
      canManageTeam: false,
      canManageBilling: false,
      canManageClients: false,
      canViewAnalytics: false,
      canCreateOrders: true,
      canApproveOrders: false,
    },
    status: 'pending',
    invitedAt: '2024-08-18T09:30:00Z',
    invitedBy: 'user-001',
    lastActiveAt: '2024-08-18T09:30:00Z',
    department: 'Content',
    jobTitle: 'Content Specialist',
    accessLevel: 60,
    loginCount: 0,
  },
];

// Clients (for agency accounts)
export const mockClients: Client[] = [
  {
    id: 'client-001',
    companyName: 'TechFlow Solutions',
    contactName: 'Robert Anderson',
    email: 'robert@techflowsolutions.com',
    phone: '+1-555-987-6543',
    website: 'https://techflowsolutions.com',
    industry: 'Software Development',
    companySize: '50-100 employees',
    status: 'active',
    servicesActive: ['Link Building', 'Content Creation', 'SEO Audit'],
    monthlySpend: 4500,
    totalSpend: 54000,
    accountManager: 'michael.chen@digitalboost.agency',
    onboardedAt: '2023-07-20T10:00:00Z',
    lastOrderAt: '2024-08-18T14:30:00Z',
    lastContactAt: '2024-08-19T10:15:00Z',
    brandingEnabled: true,
    performance: {
      totalOrders: 48,
      completedOrders: 46,
      averageOrderValue: 1125,
      satisfactionScore: 4.8,
      responseTime: 2.1,
      deliveryRate: 95.8,
      qualityScore: 4.9,
      retentionRate: 100,
    },
  },
  {
    id: 'client-002',
    companyName: 'GreenLeaf Organics',
    contactName: 'Maria Santos',
    email: 'maria@greenleaforganics.com',
    website: 'https://greenleaforganics.com',
    industry: 'E-commerce',
    companySize: '10-50 employees',
    status: 'active',
    servicesActive: ['Guest Posts', 'Visual Links', 'Content Writing'],
    monthlySpend: 2800,
    totalSpend: 33600,
    accountManager: 'jessica.rodriguez@digitalboost.agency',
    onboardedAt: '2023-09-12T11:15:00Z',
    lastOrderAt: '2024-08-17T09:45:00Z',
    lastContactAt: '2024-08-18T14:20:00Z',
    brandingEnabled: false,
    performance: {
      totalOrders: 36,
      completedOrders: 34,
      averageOrderValue: 933,
      satisfactionScore: 4.6,
      responseTime: 1.8,
      deliveryRate: 94.4,
      qualityScore: 4.7,
      retentionRate: 100,
    },
  },
  {
    id: 'client-003',
    companyName: 'FinanceFirst Consulting',
    contactName: 'James Mitchell',
    email: 'james@financefirst.co',
    phone: '+1-555-456-7890',
    website: 'https://financefirst.co',
    industry: 'Financial Services',
    companySize: '100-250 employees',
    status: 'paused',
    servicesActive: [],
    monthlySpend: 0,
    totalSpend: 18900,
    accountManager: 'michael.chen@digitalboost.agency',
    onboardedAt: '2024-02-05T13:20:00Z',
    lastOrderAt: '2024-07-30T10:15:00Z',
    lastContactAt: '2024-08-01T16:30:00Z',
    brandingEnabled: false,
    performance: {
      totalOrders: 21,
      completedOrders: 19,
      averageOrderValue: 900,
      satisfactionScore: 4.2,
      responseTime: 3.2,
      deliveryRate: 90.5,
      qualityScore: 4.3,
      retentionRate: 0,
    },
  },
];

// Billing information
export const mockBillingInfo: BillingInfo = {
  paymentMethods: [
    {
      id: 'pm-001',
      type: 'card',
      isDefault: true,
      lastFour: '4242',
      expiryDate: '12/26',
      cardBrand: 'Visa',
      status: 'active',
      addedAt: '2023-03-20T10:30:00Z',
      lastUsed: '2024-08-15T14:22:00Z',
      verificationStatus: 'verified',
    },
    {
      id: 'pm-002',
      type: 'bank',
      isDefault: false,
      bankName: 'Chase Business Banking',
      status: 'active',
      addedAt: '2023-06-15T09:15:00Z',
      lastUsed: '2024-07-30T11:45:00Z',
      verificationStatus: 'verified',
    },
    {
      id: 'pm-003',
      type: 'airwallex',
      isDefault: false,
      status: 'active',
      addedAt: '2024-01-10T13:20:00Z',
      verificationStatus: 'verified',
    },
  ],
  billingAddress: {
    street: '123 Business Ave, Suite 400',
    city: 'New York',
    state: 'NY',
    postalCode: '10001',
    country: 'United States',
    isDefault: true,
    type: 'billing',
  },
  taxInfo: {
    taxId: '12-3456789',
    taxType: 'EIN',
    isBusinessEntity: true,
    taxExempt: false,
    jurisdiction: 'United States',
  },
  invoicePreferences: {
    deliveryMethod: 'both',
    format: 'pdf',
    includeDetails: true,
    includeTaxBreakdown: true,
    autoDownload: false,
    ccEmails: ['accounting@digitalboost.agency'],
    branding: true,
    language: 'English',
  },
  subscription: {
    id: 'sub-001',
    planName: 'Agency Pro',
    planPrice: 2999,
    billingCycle: 'monthly',
    status: 'active',
    currentPeriodStart: '2024-08-15T00:00:00Z',
    currentPeriodEnd: '2024-09-15T00:00:00Z',
    renewalDate: '2024-09-15T00:00:00Z',
    usage: {
      current: 1250,
      limit: 2000,
      overage: 0,
      resetDate: '2024-09-15T00:00:00Z',
    },
    addOns: [
      {
        id: 'addon-001',
        name: 'Extra API Calls',
        price: 299,
        quantity: 2,
        isActive: true,
      },
      {
        id: 'addon-002',
        name: 'Priority Support',
        price: 199,
        quantity: 1,
        isActive: true,
      },
    ],
  },
  creditBalance: 850,
  spendingLimits: [
    {
      type: 'monthly',
      amount: 10000,
      currentSpend: 4750,
      isActive: true,
      alertThreshold: 8000,
      notifyEmails: ['sarah.johnson@digitalboost.agency', 'finance@digitalboost.agency'],
    },
  ],
};

// Notification preferences (mock - no longer used, preferences are fetched from API)
export const mockNotificationPreferences: NotificationPreferences = {
  email_enabled: true,
  push_enabled: true,
  websocket_enabled: true,
  frequency: 'realtime',
  quiet_hours_start: '22:00',
  quiet_hours_end: '07:00',
  timezone: 'America/New_York',
  categories: ['order', 'payment', 'ticket', 'task', 'system', 'site', 'dispute'],
  email_categories: ['order', 'payment', 'ticket', 'system'],
  push_categories: ['order', 'ticket', 'system'],
  disabled_types: [],
  digest_mode: false,
  sound_enabled: true,
  desktop_enabled: true,
};

// Security settings
export const mockSecuritySettings: SecuritySettings = {
  passwordLastChanged: '2024-07-15T10:30:00Z',
  twoFactorAuth: {
    enabled: true,
    method: 'app',
    backupCodes: 8,
    setupAt: '2023-04-10T14:20:00Z',
    lastUsed: '2024-08-20T14:22:00Z',
  },
  loginSessions: [
    {
      id: 'session-001',
      device: 'Chrome on MacBook Pro',
      browser: 'Chrome 127.0',
      os: 'macOS 14.5',
      ipAddress: '192.168.1.100',
      location: 'New York, NY',
      country: 'United States',
      loginTime: '2024-08-20T14:22:00Z',
      lastActive: '2024-08-20T15:30:00Z',
      isCurrent: true,
      isTrusted: true,
      riskScore: 10,
    },
    {
      id: 'session-002',
      device: 'Safari on iPhone',
      browser: 'Safari 17.5',
      os: 'iOS 17.5.1',
      ipAddress: '192.168.1.101',
      location: 'New York, NY',
      country: 'United States',
      loginTime: '2024-08-19T09:15:00Z',
      lastActive: '2024-08-19T18:45:00Z',
      isCurrent: false,
      isTrusted: true,
      riskScore: 15,
    },
    {
      id: 'session-003',
      device: 'Edge on Windows PC',
      browser: 'Edge 127.0',
      os: 'Windows 11',
      ipAddress: '10.0.0.50',
      location: 'Brooklyn, NY',
      country: 'United States',
      loginTime: '2024-08-18T13:20:00Z',
      lastActive: '2024-08-18T17:30:00Z',
      isCurrent: false,
      isTrusted: false,
      riskScore: 35,
    },
  ],
  securityQuestions: true,
  recoveryEmail: 'sarah.backup@gmail.com',
  recoveryPhone: '+1-555-987-1234',
  emergencyContacts: [
    {
      id: 'contact-001',
      name: 'Michael Chen',
      email: 'michael.chen@digitalboost.agency',
      phone: '+1-555-234-5678',
      relationship: 'Business Partner',
      verified: true,
      trustLevel: 95,
      addedAt: '2023-04-15T10:30:00Z',
    },
    {
      id: 'contact-002',
      name: 'Jessica Rodriguez',
      email: 'jessica.rodriguez@digitalboost.agency',
      phone: '+1-555-345-6789',
      relationship: 'Team Lead',
      verified: true,
      trustLevel: 85,
      addedAt: '2023-05-20T14:15:00Z',
    },
  ],
  trustedDevices: [
    {
      id: 'device-001',
      name: 'MacBook Pro 16-inch',
      deviceType: 'Desktop',
      browser: 'Chrome',
      os: 'macOS',
      fingerprint: 'fp_mac_chrome_001',
      addedAt: '2023-03-15T10:30:00Z',
      lastUsed: '2024-08-20T15:30:00Z',
      trustLevel: 100,
      isActive: true,
    },
    {
      id: 'device-002',
      name: 'iPhone 15 Pro',
      deviceType: 'Mobile',
      browser: 'Safari',
      os: 'iOS',
      fingerprint: 'fp_ios_safari_002',
      addedAt: '2023-03-20T09:15:00Z',
      lastUsed: '2024-08-19T18:45:00Z',
      trustLevel: 90,
      isActive: true,
    },
  ],
};

// Profile statistics
export const mockProfileStats: ProfileStats = {
  accountAge: 517, // days
  profileCompleteness: 95,
  lastLoginDaysAgo: 0,
  totalLogins: 1247,
  totalOrders: 324,
  completedOrders: 308,
  totalSpent: 45780,
  teamSize: 4,
  activeTeamMembers: 3,
  clientCount: 23,
  activeClients: 21,
  apiCalls: 12500,
  storageUsed: 2.4, // GB
  storageLimit: 10, // GB
  lastBackup: '2024-08-19T02:00:00Z',
  securityScore: 87,
  uptimePercentage: 99.9,
};

// Profile navigation sections
export const profileSections = [
  {
    id: 'overview',
    label: 'Profile Overview',
    icon: 'User',
    description: 'Account summary and key metrics',
    roles: ['individual', 'team', 'agency'],
  },
  {
    id: 'personal',
    label: 'Personal Information',
    icon: 'UserCircle',
    description: 'Edit your personal details',
    roles: ['individual', 'team', 'agency'],
  },
  {
    id: 'security',
    label: 'Security & Privacy',
    icon: 'Shield',
    description: 'Password, 2FA, and security settings',
    roles: ['individual', 'team', 'agency'],
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: 'Bell',
    description: 'Email and push notification preferences',
    roles: ['individual', 'team', 'agency'],
  },
  {
    id: 'billing',
    label: 'Billing & Payments',
    icon: 'CreditCard',
    description: 'Payment methods and billing information',
    roles: ['individual', 'team', 'agency'],
  },
  {
    id: 'team',
    label: 'Team Management',
    icon: 'Users',
    description: 'Manage team members and permissions',
    roles: ['team', 'agency'],
  },
  {
    id: 'clients',
    label: 'Client Management',
    icon: 'Building',
    description: 'Manage client accounts and relationships',
    roles: ['agency'],
  },
  {
    id: 'whitelabel',
    label: 'White-Label Settings',
    icon: 'Palette',
    description: 'Customize branding for client reports',
    roles: ['agency'],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: 'Link',
    description: 'Connect with third-party services',
    roles: ['team', 'agency'],
  },
  {
    id: 'api',
    label: 'API & Webhooks',
    icon: 'Code',
    description: 'Manage API keys and webhooks',
    roles: ['team', 'agency'],
  },
  {
    id: 'data',
    label: 'Data & Privacy',
    icon: 'Download',
    description: 'Export data and manage privacy',
    roles: ['individual', 'team', 'agency'],
  },
  {
    id: 'advanced',
    label: 'Advanced Settings',
    icon: 'Settings',
    description: 'Advanced configuration options',
    roles: ['team', 'agency'],
  },
];

// Quick stats for profile overview
export const profileQuickStats = [
  {
    title: 'Account Age',
    value: '1 year, 5 months',
    icon: 'Calendar',
    color: '#4F46E5',
    subtitle: 'Member since March 2023',
  },
  {
    title: 'Total Orders',
    value: '324',
    icon: 'Package',
    color: '#059669',
    subtitle: '95% completion rate',
  },
  {
    title: 'Team Size',
    value: '4',
    icon: 'Users',
    color: '#3B82F6',
    subtitle: '3 active, 1 pending',
  },
  {
    title: 'Active Clients',
    value: '21',
    icon: 'Building',
    color: '#F59E0B',
    subtitle: '$7,300 monthly revenue',
  },
];

// Recent activity log
export const recentProfileActivity: ActivityLog[] = [
  {
    id: 'activity-001',
    timestamp: '2024-08-20T10:30:00Z',
    userId: 'user-001',
    type: 'password_changed',
    category: 'security',
    title: 'Password Updated',
    description: 'You successfully updated your account password',
    icon: 'Shield',
    color: '#059669',
    severity: 'medium',
    metadata: { method: 'manual', strength: 'strong' },
    ipAddress: '192.168.1.100',
    location: 'New York, NY',
    isVisible: true,
  },
  {
    id: 'activity-002',
    timestamp: '2024-08-18T09:30:00Z',
    userId: 'user-001',
    type: 'team_member_invited',
    category: 'team',
    title: 'Team Member Invited',
    description: 'Emma Garcia was invited to join the team as Content Editor',
    icon: 'UserPlus',
    color: '#3B82F6',
    severity: 'low',
    metadata: { inviteeEmail: 'emma.garcia@gmail.com', role: 'editor' },
    ipAddress: '192.168.1.100',
    location: 'New York, NY',
    isVisible: true,
  },
  {
    id: 'activity-003',
    timestamp: '2024-08-15T14:15:00Z',
    userId: 'user-001',
    type: 'payment_method_added',
    category: 'billing',
    title: 'Payment Method Updated',
    description: 'Visa ending in 4242 was successfully updated',
    icon: 'CreditCard',
    color: '#F59E0B',
    severity: 'low',
    metadata: { type: 'card', last4: '4242', brand: 'visa' },
    ipAddress: '192.168.1.100',
    location: 'New York, NY',
    isVisible: true,
  },
  {
    id: 'activity-004',
    timestamp: '2024-08-12T11:45:00Z',
    userId: 'user-001',
    type: 'client_added',
    category: 'client',
    title: 'New Client Added',
    description: 'TechFlow Solutions was successfully onboarded',
    icon: 'Building',
    color: '#10B981',
    severity: 'low',
    metadata: {
      clientName: 'TechFlow Solutions',
      accountManager: 'michael.chen@digitalboost.agency',
    },
    ipAddress: '192.168.1.100',
    location: 'New York, NY',
    isVisible: true,
  },
  {
    id: 'activity-005',
    timestamp: '2024-08-10T16:20:00Z',
    userId: 'user-001',
    type: 'login_successful',
    category: 'security',
    title: 'Successful Login',
    description: 'Logged in from new device: iPhone 15 Pro',
    icon: 'Shield',
    color: '#8B5CF6',
    severity: 'low',
    metadata: { device: 'iPhone 15 Pro', browser: 'Safari', trusted: true },
    ipAddress: '192.168.1.101',
    location: 'New York, NY',
    isVisible: true,
  },
];
